import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PunchOverrideReason } from '@construction/shared';
import { Alert, Button, Input } from '@/components/ui';
import { PUNCH_OVERRIDE_KEY } from '@/lib/punch-labels';

interface Props {
  /** 为什么打不了卡：拿不到定位，还是人在围栏外 */
  kind: 'NO_LOCATION' | 'OUT_OF_RANGE';
  distanceM: number | null;
  allowedM: number;
  onCancel: () => void;
  onConfirm: (reason: PunchOverrideReason, note: string) => void;
  submitting: boolean;
}

/**
 * 「继续打卡」：定位失败或人在围栏外时，工人填个理由照样能打卡（CR-015）。
 *
 * 在此之前，拿不到定位就**根本打不了卡** —— 而室内、地下室、车库收不到 GPS
 * 是工地上的日常。让人干瞪眼不如让他说明情况，把判断权交给管理员。
 *
 * 提交后进待复核，**不是直接通过**。这一点必须在界面上说清楚，
 * 否则工人以为打完就万事大吉，月底才发现这天没算上。
 */
export function ContinuePunchSheet({
  kind,
  distanceM,
  allowedM,
  onCancel,
  onConfirm,
  submitting,
}: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<PunchOverrideReason | null>(null);
  const [note, setNote] = useState('');

  // 选了理由就能提交，说明**选填**（CR-070）。改之前选「其他」必须写至少 2 个字：
  // 安卓上搜狗这类中文输入法的字进不了输入框（真机实测，DOM 里一直是空的），
  // 工人被一个填不上的必填项卡在打卡这一步 —— 客户看过别的软件后拍板「输入框只是选项」
  const showNote = reason === PunchOverrideReason.OTHER;
  const canSubmit = reason != null;

  // 按场景排序：拿不到定位时，信号和设备类的原因排前面；
  // 超出范围时，位置类的原因排前面。让工人第一眼就看到最可能的那条。
  const ordered =
    kind === 'NO_LOCATION'
      ? ([
          PunchOverrideReason.INDOOR_OR_UNDERGROUND,
          PunchOverrideReason.GPS_UNAVAILABLE,
          PunchOverrideReason.PHONE_ISSUE,
          PunchOverrideReason.WORKING_OUTSIDE_FENCE,
          PunchOverrideReason.OFFSITE_TASK,
          PunchOverrideReason.SITE_LOCATION_WRONG,
          PunchOverrideReason.OTHER,
        ] as const)
      : ([
          PunchOverrideReason.WORKING_OUTSIDE_FENCE,
          PunchOverrideReason.OFFSITE_TASK,
          PunchOverrideReason.SITE_LOCATION_WRONG,
          PunchOverrideReason.GPS_UNAVAILABLE,
          PunchOverrideReason.INDOOR_OR_UNDERGROUND,
          PunchOverrideReason.PHONE_ISSUE,
          PunchOverrideReason.OTHER,
        ] as const);

  return (
    <div className="fixed inset-0 z-sheet flex flex-col justify-end bg-black/40">
      <div className="safe-bottom max-h-[88vh] overflow-y-auto rounded-t-2xl bg-surface px-4 pb-4 pt-5">
        <h2 className="text-title font-semibold tracking-tight">{t('punch.continueTitle')}</h2>
        <p className="mt-1 text-body leading-relaxed text-ink-2">
          {kind === 'NO_LOCATION'
            ? t('punch.continueNoLocation')
            : t('punch.continueOutOfRange', {
                distance: distanceM == null ? '—' : Math.round(distanceM),
                allowed: allowedM,
              })}
        </p>

        {/* 后果说明必须在选理由之前就看到，不然工人会以为打完就算数了 */}
        <div className="mt-3">
          <Alert tone="info">{t('punch.continueWillBeReviewed')}</Alert>
        </div>

        <ul className="mt-4 flex flex-col gap-2">
          {ordered.map((code) => (
            <li key={code}>
              <button
                type="button"
                onClick={() => setReason(code)}
                aria-pressed={reason === code}
                className={[
                  // 至少 56px 高：工地上戴着手套也要点得准
                  'flex min-h-row w-full items-center gap-3 rounded-lg border px-4 py-3 text-left',
                  'text-title leading-snug transition-colors',
                  reason === code
                    ? 'border-brand-600 bg-brand-50 font-medium text-brand-700'
                    : 'border-rule text-ink-2',
                ].join(' ')}
              >
                <span
                  aria-hidden
                  className={[
                    'h-4 w-4 shrink-0 rounded-full border-2',
                    reason === code ? 'border-brand-600 bg-brand-600' : 'border-rule',
                  ].join(' ')}
                />
                {t(PUNCH_OVERRIDE_KEY[code]!)}
              </button>
            </li>
          ))}
        </ul>

        {showNote && (
          <div className="mt-3">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('punch.continueNotePlaceholder')}
              maxLength={500}
              autoFocus
            />
          </div>
        )}

        <div className="mt-4 flex gap-3">
          <Button
            block
            disabled={!canSubmit}
            loading={submitting}
            onClick={() => reason && onConfirm(reason, note.trim())}
          >
            {t('punch.continueConfirm')}
          </Button>
          <Button block variant="secondary" onClick={onCancel}>
            {t('app.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
